import React from 'react';
import {
  BookOutlined,
  HomeOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import clsx from 'clsx';
import { Locale } from '../../context/AppProvider';
import { useEscDossierContext } from '../DossierContext';
import { resolveWikis, type WikiId } from './wikis';
import styles from './WikiNav.module.scss';
import WikiPageView from './WikiPageView';

interface Props {
  dossierId: string;
}

const WikiTab: React.FC<Props> = ({ dossierId }) => {
  const { messages } = React.useContext(Locale);
  const { claim } = useEscDossierContext();
  const [wikiId, setWikiId] = React.useState<WikiId>('dossier');
  const [showIndex, setShowIndex] = React.useState(false);

  const wikis = resolveWikis(claim?.has_guidelines);
  const wiki = wikis.find((w) => w.id === wikiId) ?? wikis[0];
  const hasIndex = !!wiki.index;
  const rawPath = showIndex && wiki.index ? wiki.index : wiki.overview;

  const switchWiki = (id: WikiId) => {
    setShowIndex(false);
    setWikiId(id);
  };

  return (
    <div className={styles.wiki}>
      <div className={styles.nav}>
        {wikis.length > 1 && (
          <div className={styles.switch} role="tablist">
            {wikis.map((w) => (
              <button
                type="button"
                role="tab"
                key={w.id}
                className={clsx(
                  styles.switchBtn,
                  wikiId === w.id && styles.active
                )}
                onClick={() => switchWiki(w.id)}
              >
                <BookOutlined />
                <span>{messages[w.labelKey] ?? w.labelFallback}</span>
              </button>
            ))}
          </div>
        )}
        <div className={styles.quicklinks}>
          <button
            type="button"
            className={clsx(styles.quicklink, !showIndex && styles.active)}
            onClick={() => setShowIndex(false)}
          >
            <HomeOutlined />
            <span>{messages['esc.wiki.overview'] ?? 'Overview'}</span>
          </button>
          {hasIndex && (
            <button
              type="button"
              className={clsx(styles.quicklink, showIndex && styles.active)}
              onClick={() => setShowIndex(true)}
            >
              <UnorderedListOutlined />
              <span>{messages['esc.wiki.index'] ?? 'Index'}</span>
            </button>
          )}
        </div>
      </div>
      <div className={styles.pane}>
        <WikiPageView
          key={`${wikiId}:${rawPath}`}
          dossierId={dossierId}
          rawPath={rawPath}
        />
      </div>
    </div>
  );
};

export default WikiTab;
